import { useState } from "react";
import { X, ChevronDown, ChevronUp, Activity, Eye, Zap, CheckCircle, AlertCircle, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

export type ScriptStatus = "loading" | "loaded" | "error";

export interface AnalyticsEntry {
  id: string;
  type: "pageview" | "event";
  timestamp: Date;
  data: {
    path?: string;
    title?: string;
    eventName?: string;
    eventParams?: Record<string, any>;
    source?: "ga" | "plausible";
  };
  gtagAvailable?: boolean;
}

interface DevAnalyticsDashboardProps {
  entries: AnalyticsEntry[];
  onClear: () => void;
  scriptStatus: ScriptStatus;
  measurementId: string;
  onTestPing: () => void;
}

export function DevAnalyticsDashboard({
  entries, 
  onClear, 
  scriptStatus, 
  measurementId,
  onTestPing 
}: DevAnalyticsDashboardProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const pageViews = entries.filter((e) => e.type === "pageview");
  const events = entries.filter((e) => e.type === "event");

  // Get current diagnostic info
  const gtagExists = typeof window !== "undefined" && typeof window.gtag === "function";
  const dataLayerLength = typeof window !== "undefined" && Array.isArray(window.dataLayer) 
    ? window.dataLayer.length 
    : 0;

  const getStatusIcon = () => {
    switch (scriptStatus) {
      case "loading":
        return <Loader2 className="h-3 w-3 animate-spin text-amber-500" />;
      case "loaded":
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case "error":
        return <AlertCircle className="h-3 w-3 text-destructive" />;
    }
  };

  const getStatusText = () => {
    switch (scriptStatus) {
      case "loading":
        return "Loading...";
      case "loaded":
        return "Connected";
      case "error":
        return "Failed";
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-[9999] h-10 w-10 rounded-full p-0 shadow-lg"
        variant="default"
      >
        <Activity className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-80 rounded-lg border border-border bg-background shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Analytics Debug</span>
          <Badge variant="secondary" className="text-xs">
            DEV
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Connection Status Bar */}
          <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5">
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <span className="text-xs font-medium">{getStatusText()}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 text-[10px] px-2"
              onClick={() => setShowDiagnostics(!showDiagnostics)}
            >
              {showDiagnostics ? "Hide" : "Diagnostics"}
            </Button>
          </div>

          {/* Diagnostics Panel */}
          {showDiagnostics && (
            <div className="border-b border-border bg-muted/50 px-3 py-2 space-y-1.5">
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div className="flex items-center justify-between rounded bg-background px-2 py-1">
                  <span className="text-muted-foreground">GA ID:</span>
                  <code className="font-mono text-foreground">{measurementId}</code>
                </div>
                <div className="flex items-center justify-between rounded bg-background px-2 py-1">
                  <span className="text-muted-foreground">Script:</span>
                  <span className={scriptStatus === "loaded" ? "text-green-500" : scriptStatus === "error" ? "text-destructive" : "text-amber-500"}>
                    {scriptStatus}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded bg-background px-2 py-1">
                  <span className="text-muted-foreground">gtag():</span>
                  {gtagExists ? (
                    <span className="text-green-500">✓ Available</span>
                  ) : (
                    <span className="text-destructive">✗ Missing</span>
                  )}
                </div>
                <div className="flex items-center justify-between rounded bg-background px-2 py-1">
                  <span className="text-muted-foreground">dataLayer:</span>
                  <span className="text-foreground">{dataLayerLength} items</span>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="w-full h-6 text-xs gap-1.5"
                onClick={onTestPing}
              >
                <Send className="h-3 w-3" />
                Send Test Ping
              </Button>
            </div>
          )}

          {/* Stats */}
          <div className="flex gap-4 border-b border-border px-3 py-2">
            <div className="flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Page Views: <strong className="text-foreground">{pageViews.length}</strong>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Events: <strong className="text-foreground">{events.length}</strong>
              </span>
            </div>
          </div>

          {/* Entries List */}
          <ScrollArea className="h-64">
            <div className="p-2">
              {entries.length === 0 ? (
                <p className="py-8 text-center text-xs text-muted-foreground">
                  No analytics events yet.
                  <br />
                  Navigate to track page views.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {entries
                    .slice()
                    .reverse()
                    .map((entry) => (
                      <div
                        key={entry.id}
                        className="rounded-md border border-border bg-muted/50 p-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            {entry.type === "pageview" ? (
                              <Eye className="h-3 w-3 text-blue-500" />
                            ) : (
                              <Zap className="h-3 w-3 text-amber-500" />
                            )}
                            <Badge
                              variant={entry.type === "pageview" ? "default" : "secondary"}
                              className="text-[10px] px-1.5 py-0"
                            >
                              {entry.type === "pageview" ? "PAGE" : "EVENT"}
                            </Badge>
                            {entry.data.source && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                {entry.data.source.toUpperCase()}
                              </Badge>
                            )}
                            {/* gtag availability indicator */}
                            {entry.gtagAvailable !== undefined && (
                              entry.gtagAvailable ? (
                                <CheckCircle className="h-2.5 w-2.5 text-green-500" />
                              ) : (
                                <AlertCircle className="h-2.5 w-2.5 text-amber-500" />
                              )
                            )}
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            {entry.timestamp.toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="mt-1">
                          {entry.type === "pageview" ? (
                            <>
                              <p className="truncate text-xs font-medium">
                                {entry.data.path}
                              </p>
                              <p className="truncate text-[10px] text-muted-foreground">
                                {entry.data.title}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-xs font-medium">{entry.data.eventName}</p>
                              {entry.data.eventParams && (
                                <pre className="mt-1 overflow-x-auto text-[10px] text-muted-foreground">
                                  {JSON.stringify(entry.data.eventParams, null, 2)}
                                </pre>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="flex items-center justify-center border-t border-border px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={onClear}
            >
              Clear All
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
